import { useState, useRef, useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { useTranscribeAudioMutation } from '../api/aiApi';

/**
 * Hook for recording audio and transcribing via the backend whisper service.
 * Exposes an AnalyserNode ref for real-time waveform visualization.
 */
export function useVoiceInput({ onTranscription, projectUuid = null }) {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const recordingStartedAtRef = useRef(null);

  const [transcribeAudio] = useTranscribeAudioMutation();

  const cleanup = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    audioContextRef.current?.close();
    streamRef.current = null;
    audioContextRef.current = null;
    analyserRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const stopAndTranscribe = useCallback(async () => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state === 'inactive') return;

    const blob = await new Promise((resolve) => {
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
        resolve(audioBlob);
      };
      mediaRecorder.stop();
    });

    cleanup();
    setIsRecording(false);

    if (blob.size === 0) {
      setError(t('ai.voice.noAudio'));
      return;
    }

    setIsTranscribing(true);
    setError(null);
    try {
      const audioSeconds = recordingStartedAtRef.current
        ? Math.max(0, (Date.now() - recordingStartedAtRef.current) / 1000)
        : 0;
      const result = await transcribeAudio({ audioBlob: blob, projectUuid, audioSeconds }).unwrap();
      if (result.text?.trim()) {
        onTranscription(result.text.trim());
      }
    } catch (err) {
      setError(err?.data?.detail || t('ai.voice.transcriptionFailed'));
    } finally {
      setIsTranscribing(false);
    }
  }, [transcribeAudio, onTranscription, cleanup, projectUuid, t]);

  const startRecording = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Set up Web Audio analyser for waveform visualization
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });
      mediaRecorderRef.current = mediaRecorder;
      recordingStartedAtRef.current = Date.now();
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.start(250);
      setIsRecording(true);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError(t('ai.voice.microphoneDenied'));
      } else {
        setError(t('ai.voice.microphoneUnavailable'));
      }
    }
  }, [t]);

  const cancelRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    cleanup();
    setIsRecording(false);
    setError(null);
  }, [cleanup]);

  return {
    isRecording,
    isTranscribing,
    error,
    analyserRef,
    startRecording,
    stopAndTranscribe,
    cancelRecording,
  };
}
